import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Users, ClipboardList, Calendar, LogOut, Plus, Pencil, Trash2, CheckCircle2, Download, FileText, Paperclip, DollarSign, Clock, Truck, Upload, Eye } from 'lucide-react';
import { FleetTrackingTab, BadgeAssignmentModal, SalarySlipsAdminTab } from './EmployeeAdminComponents';
import { EmployeeMonitoringModal } from './EmployeeMonitoringModal';
import { DutyManagementTab, EmployeeFilesAdminTab } from './DutyFilesAdmin';
import { EmployeeKYCAdminView } from './EmployeeKYCAdminView';

var API_URL = process.env.REACT_APP_BACKEND_URL;

export function EmployeeAdminDashboard() {
  var navigate = useNavigate();
  var [activeTab, setActiveTab] = useState('overview');
  var [stats, setStats] = useState(null);
  var [employees, setEmployees] = useState([]);
  var [tasks, setTasks] = useState([]);
  var [slots, setSlots] = useState([]);
  var [predefinedSlots, setPredefinedSlots] = useState([]);
  var [pendingApprovals, setPendingApprovals] = useState([]);
  var [fleetDashboard, setFleetDashboard] = useState(null);
  var [loading, setLoading] = useState(true);
  var [searchTerm, setSearchTerm] = useState('');
  var [showEmployeeModal, setShowEmployeeModal] = useState(false);
  var [showTaskModal, setShowTaskModal] = useState(false);
  var [showSlotModal, setShowSlotModal] = useState(false);
  var [showReportModal, setShowReportModal] = useState(false);
  var [showBadgeModal, setShowBadgeModal] = useState(false);
  var [showMonitorModal, setShowMonitorModal] = useState(false);
  var [monitorEmployeeId, setMonitorEmployeeId] = useState(null);
  var [selectedFleetManager, setSelectedFleetManager] = useState(null);
  var [editingEmployee, setEditingEmployee] = useState(null);
  var [editingTask, setEditingTask] = useState(null);
  var [employeeForm, setEmployeeForm] = useState({ name: '', phone: '', employee_type: 'hr', department: 'HR', designation: '' });
  var [taskForm, setTaskForm] = useState({ 
    employee_id: '', title: '', description: '', priority: 'medium', due_date: '',
    business_name: '', business_type: 'restaurant', business_location: '', contact_number: '', procedure_type: '', target_count: '', attachment: null
  });
  var [slotDate, setSlotDate] = useState('');
  var [slotSeats, setSlotSeats] = useState({});
  var [reportData, setReportData] = useState(null);
  var [reportDates, setReportDates] = useState({ start: '', end: '' });

  useEffect(function() {
    var token = localStorage.getItem('employee_admin_token');
    if (!token) { navigate('/employee-admin'); return; }
    var today = new Date();
    var week = new Date(today.getTime() - 7*24*60*60*1000);
    var tomorrow = new Date(today.getTime() + 24*60*60*1000);
    setReportDates({ start: week.toISOString().split('T')[0], end: today.toISOString().split('T')[0] });
    setSlotDate(tomorrow.toISOString().split('T')[0]);
    fetchData();
    fetchPredefinedSlots();
  }, [navigate]);

  function fetchData() {
    setLoading(true);
    fetch(API_URL + '/api/employee-admin/stats').then(function(r) { return r.json(); }).then(function(d) { setStats(d); });
    fetch(API_URL + '/api/employee-admin/employees').then(function(r) { return r.json(); }).then(function(d) { setEmployees(d); });
    fetch(API_URL + '/api/employee-admin/tasks').then(function(r) { return r.json(); }).then(function(d) { setTasks(d); });
    fetch(API_URL + '/api/employee-admin/slots').then(function(r) { return r.json(); }).then(function(d) { setSlots(d); setLoading(false); });
    fetch(API_URL + '/api/employee-admin/pending-approvals').then(function(r) { return r.json(); }).then(function(d) { setPendingApprovals(d); });
    fetch(API_URL + '/api/employee-admin/fleet-dashboard').then(function(r) { return r.json(); }).then(function(d) { setFleetDashboard(d); });
  }

  function fetchPredefinedSlots() {
    fetch(API_URL + '/api/employee-admin/predefined-slots').then(function(r) { return r.json(); }).then(function(d) { 
      setPredefinedSlots(d.slots || []);
      var defaultSeats = {};
      for (var i = 0; i < (d.slots || []).length; i++) {
        defaultSeats[i] = 5;
      }
      setSlotSeats(defaultSeats);
    });
  }

  function handleLogout() { localStorage.removeItem('employee_admin_token'); navigate('/employee-admin'); }

  function handleSaveEmployee() {
    var url = editingEmployee ? API_URL + '/api/employee-admin/employees/' + editingEmployee.id : API_URL + '/api/employee-admin/employees';
    fetch(url, { method: editingEmployee ? 'PUT' : 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(employeeForm) })
      .then(function(r) { 
        if (!r.ok) return r.json().then(function(d) { 
          // Handle both string and array error formats
          var errorMsg = d.detail;
          if (Array.isArray(errorMsg)) {
            errorMsg = errorMsg.map(function(e) { return e.msg || e.message || JSON.stringify(e); }).join(', ');
          } else if (typeof errorMsg === 'object') {
            errorMsg = JSON.stringify(errorMsg);
          }
          throw new Error(errorMsg || 'Failed to save employee'); 
        }); 
        return r.json(); 
      })
      .then(function() { toast.success('Saved!'); setShowEmployeeModal(false); setEmployeeForm({ name: '', phone: '', employee_type: 'hr', department: 'HR', designation: '' }); setEditingEmployee(null); fetchData(); })
      .catch(function(e) { toast.error(e.message || 'An error occurred'); });
  }

  function handleDeleteEmployee(id) { if (!window.confirm('Delete?')) return; fetch(API_URL + '/api/employee-admin/employees/' + id, { method: 'DELETE' }).then(function() { toast.success('Deleted'); fetchData(); }); }

  function handleSaveTask() {
    var url = editingTask ? API_URL + '/api/employee-admin/tasks/' + editingTask.id : API_URL + '/api/employee-admin/tasks';
    
    // Create task first
    var taskData = Object.assign({}, taskForm);
    delete taskData.attachment;
    
    fetch(url, { method: editingTask ? 'PUT' : 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(taskData) })
      .then(function(r) { if (!r.ok) return r.json().then(function(d) { throw new Error(d.detail); }); return r.json(); })
      .then(function(result) { 
        var taskId = editingTask ? editingTask.id : result.task_id;
        
        // Upload attachment if present
        if (taskForm.attachment && taskId) {
          var formData = new FormData();
          formData.append('file', taskForm.attachment);
          formData.append('uploaded_by', 'admin');
          
          return fetch(API_URL + '/api/tasks/' + taskId + '/attachments', {
            method: 'POST',
            body: formData
          }).then(function() { return result; });
        }
        return result;
      })
      .then(function() { 
        toast.success('Saved!'); 
        setShowTaskModal(false); 
        setTaskForm({ 
          employee_id: '', title: '', description: '', priority: 'medium', due_date: '',
          business_name: '', business_type: 'restaurant', business_location: '', contact_number: '', procedure_type: 'first_call', attachment: null
        }); 
        setEditingTask(null); 
        fetchData(); 
      })
      .catch(function(e) { toast.error(e.message); });
  }

  function handleDeleteTask(id) { if (!window.confirm('Delete?')) return; fetch(API_URL + '/api/employee-admin/tasks/' + id, { method: 'DELETE' }).then(function() { toast.success('Deleted'); fetchData(); }); }

  function handleCreateDailySlots() {
    var slotsToCreate = [];
    for (var idx in slotSeats) {
      if (slotSeats[idx] > 0) {
        slotsToCreate.push({ slot_index: parseInt(idx), max_bookings: slotSeats[idx] });
      }
    }
    if (slotsToCreate.length === 0) {
      toast.error('Set seats for at least one slot');
      return;
    }
    fetch(API_URL + '/api/employee-admin/daily-slots', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ date: slotDate, slots: slotsToCreate }) })
      .then(function(r) { return r.json(); })
      .then(function() { toast.success('Slots created!'); setShowSlotModal(false); fetchData(); })
      .catch(function() { toast.error('Failed'); });
  }

  function handleDeleteSlot(id) { if (!window.confirm('Delete?')) return; fetch(API_URL + '/api/employee-admin/slots/' + id, { method: 'DELETE' }).then(function() { toast.success('Deleted'); fetchData(); }); }

  function handleApprovePayment(taskId, approved) {
    fetch(API_URL + '/api/employee-admin/tasks/' + taskId + '/approve-payment?approved=' + approved, { method: 'PUT' })
      .then(function(r) { return r.json(); })
      .then(function(d) { toast.success(approved ? 'Approved! ₹' + d.amount : 'Rejected'); fetchData(); })
      .catch(function() { toast.error('Failed'); });
  }

  function handleOpenBadgeModal(fm) {
    setSelectedFleetManager(fm);
    setShowBadgeModal(true);
  }

  function handleAssignBadge(badgeType) {
    if (!selectedFleetManager) return;
    var weekStart = new Date();
    weekStart.setDate(weekStart.getDate() - weekStart.getDay() + 1);
    var weekStartStr = weekStart.toISOString().split('T')[0];
    
    fetch(API_URL + '/api/employee-admin/fleet-badge', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        employee_id: selectedFleetManager.id,
        badge_type: badgeType,
        week_start: weekStartStr,
        reason: 'Weekly task completion'
      })
    })
      .then(function(r) { return r.json(); })
      .then(function() { toast.success('Badge assigned!'); setShowBadgeModal(false); fetchData(); })
      .catch(function() { toast.error('Failed'); });
  }

  function handleRemoveBadge(employeeId) {
    if (!window.confirm('Remove badge?')) return;
    fetch(API_URL + '/api/employee-admin/fleet-badge/' + employeeId, { method: 'DELETE' })
      .then(function() { toast.success('Badge removed'); setShowBadgeModal(false); fetchData(); });
  }

  function fetchReport() { fetch(API_URL + '/api/employee-admin/reports/attendance?start_date=' + reportDates.start + '&end_date=' + reportDates.end).then(function(r) { return r.json(); }).then(function(d) { setReportData(d); }); }
  function downloadCSV() { window.open(API_URL + '/api/employee-admin/reports/attendance/csv?start_date=' + reportDates.start + '&end_date=' + reportDates.end, '_blank'); }
  function uploadAttachment(taskId, file) { var formData = new FormData(); formData.append('file', file); fetch(API_URL + '/api/employee-admin/tasks/' + taskId + '/attachment', { method: 'POST', body: formData }).then(function() { toast.success('Uploaded!'); fetchData(); }); }

  var filteredEmployees = employees.filter(function(e) { return !searchTerm || (e.name && e.name.toLowerCase().indexOf(searchTerm.toLowerCase()) !== -1); });
  var flexEmployees = employees.filter(function(e) { return e.employee_type === 'flex_employee'; });
  var fleetManagers = employees.filter(function(e) { return e.employee_type === 'fleet_manager'; });

  if (loading) return <div className="min-h-screen bg-slate-900 flex items-center justify-center"><div className="w-8 h-8 border-4 border-purple-500/30 border-t-purple-500 rounded-full animate-spin"></div></div>;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900/20 to-slate-900" data-testid="employee-admin-dashboard">
      <header className="bg-slate-900/80 backdrop-blur-xl border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center"><Users className="w-5 h-5 text-white" /></div>
            <div><h1 className="text-xl font-bold text-white">Employee Admin</h1></div>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={function() { setShowReportModal(true); fetchReport(); }} variant="outline" className="border-white/20 text-white hover:bg-white/10"><FileText className="w-4 h-4 mr-2" />Reports</Button>
            <Button variant="ghost" onClick={handleLogout} className="text-gray-400 hover:text-white"><LogOut className="w-4 h-4 mr-2" />Logout</Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-slate-800/50 border border-white/10 mb-6 flex-wrap">
            <TabsTrigger value="overview" className="data-[state=active]:bg-purple-600" data-testid="overview-tab">Overview</TabsTrigger>
            <TabsTrigger value="employees" className="data-[state=active]:bg-purple-600" data-testid="employees-tab">Employees</TabsTrigger>
            <TabsTrigger value="tasks" className="data-[state=active]:bg-purple-600" data-testid="tasks-tab">Tasks</TabsTrigger>
            <TabsTrigger value="weeklyoff" className="data-[state=active]:bg-red-600" data-testid="weeklyoff-tab"><Calendar className="w-4 h-4 mr-1" />Weekly Off</TabsTrigger>
            <TabsTrigger value="duty" className="data-[state=active]:bg-green-600" data-testid="duty-tab"><ClipboardList className="w-4 h-4 mr-1" />Duty</TabsTrigger>
            <TabsTrigger value="empfiles" className="data-[state=active]:bg-purple-600" data-testid="empfiles-tab"><Paperclip className="w-4 h-4 mr-1" />Files</TabsTrigger>
            <TabsTrigger value="kyc" className="data-[state=active]:bg-amber-600" data-testid="kyc-tab"><FileText className="w-4 h-4 mr-1" />KYC/Bank</TabsTrigger>
            <TabsTrigger value="fleet" className="data-[state=active]:bg-blue-600" data-testid="fleet-tab"><Truck className="w-4 h-4 mr-1" />Fleet</TabsTrigger>
            <TabsTrigger value="slots" className="data-[state=active]:bg-purple-600" data-testid="slots-tab">Slots</TabsTrigger>
            <TabsTrigger value="salary" className="data-[state=active]:bg-green-600" data-testid="salary-tab"><FileText className="w-4 h-4 mr-1" />Salary Slips</TabsTrigger>
            <TabsTrigger value="payments" className="data-[state=active]:bg-purple-600" data-testid="payments-tab">Payments</TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <OverviewTab stats={stats} pendingApprovals={pendingApprovals} />
          </TabsContent>

          <TabsContent value="fleet">
            <FleetTrackingTab fleetDashboard={fleetDashboard} onAssignBadge={handleOpenBadgeModal} onMonitor={function(empId) { setMonitorEmployeeId(empId); setShowMonitorModal(true); }} />
          </TabsContent>

          <TabsContent value="employees">
            <EmployeesTab 
              filteredEmployees={filteredEmployees}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              onAdd={function() { setEditingEmployee(null); setEmployeeForm({ name: '', phone: '', employee_type: 'hr', department: 'HR', designation: '' }); setShowEmployeeModal(true); }}
              onEdit={function(emp) { setEditingEmployee(emp); setEmployeeForm({ name: emp.name, phone: emp.phone, employee_type: emp.employee_type, department: emp.department || 'HR', designation: emp.designation || '' }); setShowEmployeeModal(true); }}
              onDelete={handleDeleteEmployee}
              onMonitor={function(empId) { setMonitorEmployeeId(empId); setShowMonitorModal(true); }}
            />
          </TabsContent>

          <TabsContent value="tasks">
            <TasksTab 
              tasks={tasks}
              onAdd={function() { setEditingTask(null); setTaskForm({ employee_id: '', title: '', description: '', priority: 'medium', due_date: '', business_name: '', business_type: 'restaurant', business_location: '', contact_number: '', procedure_type: '', target_count: '' }); setShowTaskModal(true); }}
              onEdit={function(task) { setEditingTask(task); setTaskForm({ employee_id: task.employee_id, title: task.title, description: task.description, priority: task.priority, due_date: task.due_date, business_name: task.business_name || '', business_type: task.business_type || 'restaurant', business_location: task.business_location || '', contact_number: task.contact_number || '', procedure_type: task.procedure_type || '', target_count: task.target_count || '' }); setShowTaskModal(true); }}
              onDelete={handleDeleteTask}
              onUploadAttachment={uploadAttachment}
            />
          </TabsContent>

          <TabsContent value="slots">
            <SlotsTab 
              slots={slots}
              onAdd={function() { setShowSlotModal(true); }}
              onDelete={handleDeleteSlot}
            />
          </TabsContent>

          <TabsContent value="salary">
            <SalarySlipsAdminTab employees={employees.filter(function(e) { return e.employee_type !== 'flex_employee'; })} />
          </TabsContent>

          <TabsContent value="payments">
            <PaymentsTab pendingApprovals={pendingApprovals} onApprove={handleApprovePayment} />
          </TabsContent>

          <TabsContent value="weeklyoff">
            <WeeklyOffTab employees={employees} onUpdate={fetchData} />
          </TabsContent>

          <TabsContent value="duty">
            <DutyManagementTab employees={employees} />
          </TabsContent>

          <TabsContent value="empfiles">
            <EmployeeFilesAdminTab />
          </TabsContent>

          <TabsContent value="kyc">
            <EmployeeKYCAdminView />
          </TabsContent>
        </Tabs>
      </main>

      {/* Employee Modal */}
      <EmployeeModal 
        isOpen={showEmployeeModal}
        onClose={function() { setShowEmployeeModal(false); }}
        editingEmployee={editingEmployee}
        employeeForm={employeeForm}
        setEmployeeForm={setEmployeeForm}
        onSave={handleSaveEmployee}
      />

      {/* Task Modal */}
      <TaskModal 
        isOpen={showTaskModal}
        onClose={function() { setShowTaskModal(false); }}
        editingTask={editingTask}
        taskForm={taskForm}
        setTaskForm={setTaskForm}
        employees={employees}
        onSave={handleSaveTask}
      />

      {/* Slot Configuration Modal */}
      <SlotModal 
        isOpen={showSlotModal}
        onClose={function() { setShowSlotModal(false); }}
        slotDate={slotDate}
        setSlotDate={setSlotDate}
        predefinedSlots={predefinedSlots}
        slotSeats={slotSeats}
        setSlotSeats={setSlotSeats}
        onCreate={handleCreateDailySlots}
      />

      {/* Report Modal */}
      <ReportModal 
        isOpen={showReportModal}
        onClose={function() { setShowReportModal(false); }}
        reportDates={reportDates}
        setReportDates={setReportDates}
        reportData={reportData}
        onGenerate={fetchReport}
        onDownload={downloadCSV}
      />

      {/* Badge Assignment Modal */}
      <BadgeAssignmentModal 
        isOpen={showBadgeModal}
        onClose={function() { setShowBadgeModal(false); }}
        selectedFleetManager={selectedFleetManager}
        onAssignBadge={handleAssignBadge}
        onRemoveBadge={handleRemoveBadge}
      />

      {/* Employee Monitoring Modal */}
      <EmployeeMonitoringModal
        isOpen={showMonitorModal}
        onClose={function() { setShowMonitorModal(false); setMonitorEmployeeId(null); }}
        employeeId={monitorEmployeeId}
      />
    </div>
  );
}

// Overview Tab
function OverviewTab(props) {
  var stats = props.stats;
  var pendingApprovals = props.pendingApprovals;
  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
      <Card className="bg-slate-800/50 border-white/10"><CardContent className="p-6"><p className="text-gray-400 text-sm">Total Employees</p><p className="text-3xl font-bold text-white">{stats ? stats.employees.total : 0}</p></CardContent></Card>
      <Card className="bg-slate-800/50 border-white/10"><CardContent className="p-6"><p className="text-gray-400 text-sm">Currently Working</p><p className="text-3xl font-bold text-white">{stats ? stats.attendance.currently_working : 0}</p></CardContent></Card>
      <Card className="bg-slate-800/50 border-white/10"><CardContent className="p-6"><p className="text-gray-400 text-sm">Pending Tasks</p><p className="text-3xl font-bold text-white">{stats ? stats.tasks.pending : 0}</p></CardContent></Card>
      <Card className="bg-slate-800/50 border-white/10"><CardContent className="p-6"><p className="text-gray-400 text-sm">Pending Approvals</p><p className="text-3xl font-bold text-amber-400">{pendingApprovals.length}</p></CardContent></Card>
    </div>
  );
}

// Employees Tab - Organized by type
function EmployeesTab(props) {
  var filteredEmployees = props.filteredEmployees;
  var searchTerm = props.searchTerm;
  var setSearchTerm = props.setSearchTerm;
  var onAdd = props.onAdd;
  var onEdit = props.onEdit;
  var onDelete = props.onDelete;
  var onMonitor = props.onMonitor;

  // Group employees by type
  var employeeTypes = [
    { key: 'fleet_manager', label: 'Fleet Managers', color: 'blue', icon: Truck },
    { key: 'flex_employee', label: 'Flex Employees', color: 'amber', icon: Clock },
    { key: 'hr', label: 'HR', color: 'purple', icon: Users },
    { key: 'digital_marketing', label: 'Digital Marketing', color: 'pink', icon: Users },
    { key: 'sales', label: 'Sales', color: 'green', icon: Users },
    { key: 'marketing', label: 'Marketing', color: 'cyan', icon: Users },
    { key: 'accounts', label: 'Accounts', color: 'orange', icon: Users },
    { key: 'operations', label: 'Operations', color: 'teal', icon: Users }
  ];

  var sections = [];
  for (var t = 0; t < employeeTypes.length; t++) {
    var typeInfo = employeeTypes[t];
    var typeEmployees = filteredEmployees.filter(function(e) { return e.employee_type === typeInfo.key; });
    
    if (typeEmployees.length > 0) {
      var rows = [];
      for (var i = 0; i < typeEmployees.length; i++) {
        rows.push(<EmployeeRow key={typeEmployees[i].id} emp={typeEmployees[i]} onEdit={onEdit} onDelete={onDelete} onMonitor={onMonitor} />);
      }
      
      var bgColor = 'bg-' + typeInfo.color + '-500/10';
      var borderColor = 'border-' + typeInfo.color + '-500/30';
      var textColor = 'text-' + typeInfo.color + '-400';
      
      sections.push(
        <div key={typeInfo.key} className="mb-6">
          <div className={"flex items-center gap-2 mb-3 p-2 rounded-lg " + bgColor + " border " + borderColor}>
            <typeInfo.icon className={"w-5 h-5 " + textColor} />
            <h3 className={"font-semibold " + textColor}>{typeInfo.label}</h3>
            <Badge className={bgColor + " " + textColor}>{typeEmployees.length}</Badge>
          </div>
          <div className="space-y-2 pl-2">{rows}</div>
        </div>
      );
    }
  }

  return (
    <Card className="bg-slate-800/50 border-white/10">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-white">Employees</CardTitle>
        <div className="flex items-center gap-2">
          <Input placeholder="Search..." value={searchTerm} onChange={function(e) { setSearchTerm(e.target.value); }} className="bg-slate-700/50 border-white/10 text-white w-48" data-testid="employee-search" />
          <Button onClick={onAdd} className="bg-purple-600 hover:bg-purple-700" data-testid="add-employee-btn"><Plus className="w-4 h-4 mr-2" />Add</Button>
        </div>
      </CardHeader>
      <CardContent>
        {sections.length > 0 ? sections : <p className="text-gray-400 text-center py-8">No employees found. Add employees to get started.</p>}
      </CardContent>
    </Card>
  );
}

function EmployeeRow(props) {
  var emp = props.emp;
  var onEdit = props.onEdit;
  var onDelete = props.onDelete;
  var onMonitor = props.onMonitor;
  var typeBadge = emp.employee_type === 'fleet_manager' ? 'bg-blue-500/20 text-blue-300' : emp.employee_type === 'flex_employee' ? 'bg-amber-500/20 text-amber-300' : 'bg-green-500/20 text-green-300';
  return (
    <div className="flex items-center justify-between p-3 bg-slate-700/30 rounded-lg" data-testid={'employee-' + emp.id}>
      <div className="flex items-center gap-3">
        <div 
          className="cursor-pointer hover:bg-slate-600/50 p-1 rounded transition-colors"
          onClick={function() { onMonitor(emp.id); }}
          title="Click to monitor this employee"
        >
          <p className="text-white font-medium hover:text-blue-400">{emp.name}</p>
          <p className="text-sm text-gray-400">{emp.phone}</p>
        </div>
        <Badge className={typeBadge}>{emp.employee_type.replace('_', ' ')}</Badge>
      </div>
      <div className="flex gap-2">
        <Button variant="ghost" size="sm" onClick={function() { onMonitor(emp.id); }} className="text-green-400" title="Monitor Employee"><Eye className="w-4 h-4" /></Button>
        <Button variant="ghost" size="sm" onClick={function() { onEdit(emp); }} className="text-blue-400"><Pencil className="w-4 h-4" /></Button>
        <Button variant="ghost" size="sm" onClick={function() { onDelete(emp.id); }} className="text-red-400"><Trash2 className="w-4 h-4" /></Button>
      </div>
    </div>
  );
}

// Tasks Tab
function TasksTab(props) {
  var tasks = props.tasks;
  var onAdd = props.onAdd;
  var onEdit = props.onEdit;
  var onDelete = props.onDelete;
  var onUploadAttachment = props.onUploadAttachment;

  var taskItems = [];
  for (var i = 0; i < tasks.length; i++) {
    var task = tasks[i];
    taskItems.push(<TaskRow key={task.id} task={task} onEdit={onEdit} onDelete={onDelete} onUploadAttachment={onUploadAttachment} />);
  }

  return (
    <Card className="bg-slate-800/50 border-white/10">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-white">Tasks</CardTitle>
        <Button onClick={onAdd} className="bg-purple-600 hover:bg-purple-700"><Plus className="w-4 h-4 mr-2" />Add Task</Button>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">{taskItems}</div>
      </CardContent>
    </Card>
  );
}

function TaskRow(props) {
  var task = props.task;
  var onEdit = props.onEdit;
  var onDelete = props.onDelete;
  var onUploadAttachment = props.onUploadAttachment;
  var priorityRate = { low: 50, medium: 60, high: 70, urgent: 80 };
  var priorityClass = task.priority === 'urgent' ? 'bg-red-500/20 text-red-300' : task.priority === 'high' ? 'bg-orange-500/20 text-orange-300' : 'bg-blue-500/20 text-blue-300';
  var statusClass = task.status === 'completed' ? 'bg-green-500/20 text-green-300' : 'bg-yellow-500/20 text-yellow-300';
  
  return (
    <div className="p-4 bg-slate-700/30 rounded-lg" data-testid={'task-' + task.id}>
      <div className="flex items-start justify-between mb-2">
        <div><h4 className="text-white font-medium">{task.title}</h4><p className="text-sm text-gray-400">{task.employee_name} - ₹{priorityRate[task.priority] || 60}</p></div>
        <div className="flex gap-2">
          <Badge className={priorityClass}>{task.priority}</Badge>
          <Badge className={statusClass}>{task.status}</Badge>
        </div>
      </div>
      <p className="text-gray-300 text-sm mb-2">{task.description}</p>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">Due: {task.due_date}</span>
          <label className="cursor-pointer text-xs text-purple-400">
            <Paperclip className="w-3 h-3 inline mr-1" />Add File
            <input type="file" className="hidden" onChange={function(e) { if (e.target.files[0]) onUploadAttachment(task.id, e.target.files[0]); }} />
          </label>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={function() { onEdit(task); }} className="text-blue-400"><Pencil className="w-4 h-4" /></Button>
          <Button variant="ghost" size="sm" onClick={function() { onDelete(task.id); }} className="text-red-400"><Trash2 className="w-4 h-4" /></Button>
        </div>
      </div>
    </div>
  );
}

// Slots Tab
function SlotsTab(props) {
  var slots = props.slots;
  var onAdd = props.onAdd;
  var onDelete = props.onDelete;

  var slotItems = [];
  for (var i = 0; i < slots.length; i++) {
    var slot = slots[i];
    slotItems.push(<SlotCard key={slot.id} slot={slot} onDelete={onDelete} />);
  }

  return (
    <Card className="bg-slate-800/50 border-white/10">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-white flex items-center gap-2"><Calendar className="w-5 h-5 text-amber-400" />Flex Employee Slots</CardTitle>
        <Button onClick={onAdd} className="bg-amber-600 hover:bg-amber-700"><Plus className="w-4 h-4 mr-2" />Configure Day</Button>
      </CardHeader>
      <CardContent>
        <p className="text-gray-400 text-sm mb-4">Predefined time slots with seat limits. Flex employees must book 24 hours in advance.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {slotItems}
          {slots.length === 0 && <p className="text-gray-400 col-span-full text-center py-8">No slots configured. Click "Configure Day" to create slots.</p>}
        </div>
      </CardContent>
    </Card>
  );
}

function SlotCard(props) {
  var slot = props.slot;
  var onDelete = props.onDelete;
  var spotsLeft = slot.max_bookings - slot.current_bookings;
  var spotsClass = spotsLeft < 2 ? 'bg-red-500/20 text-red-300' : 'bg-green-500/20 text-green-300';
  return (
    <div className="p-4 bg-slate-700/30 rounded-lg">
      <div className="flex justify-between mb-2">
        <Badge className="bg-amber-500/20 text-amber-300">{slot.date}</Badge>
        <Button variant="ghost" size="sm" onClick={function() { onDelete(slot.id); }} className="text-red-400 p-1"><Trash2 className="w-4 h-4" /></Button>
      </div>
      <p className="text-white font-medium">{slot.label || (slot.start_time + ' - ' + slot.end_time)}</p>
      <div className="flex items-center justify-between mt-2">
        <span className="text-sm text-gray-400">{slot.current_bookings}/{slot.max_bookings} booked</span>
        <Badge className={spotsClass}>{spotsLeft} left</Badge>
      </div>
    </div>
  );
}

// Payments Tab
function PaymentsTab(props) {
  var pendingApprovals = props.pendingApprovals;
  var onApprove = props.onApprove;

  var approvalItems = [];
  for (var i = 0; i < pendingApprovals.length; i++) {
    var task = pendingApprovals[i];
    approvalItems.push(<PaymentApprovalRow key={task.id} task={task} onApprove={onApprove} />);
  }

  return (
    <Card className="bg-slate-800/50 border-white/10">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2"><DollarSign className="w-5 h-5 text-green-400" />Payment Approvals</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-gray-400 text-sm mb-4">Tasks pending payment approval. Payment is released 12 hours after approval, on Tuesdays after 12pm.</p>
        <div className="space-y-3">
          {approvalItems}
          {pendingApprovals.length === 0 && <p className="text-gray-400 text-center py-8">No pending payment approvals</p>}
        </div>
      </CardContent>
    </Card>
  );
}

function PaymentApprovalRow(props) {
  var task = props.task;
  var onApprove = props.onApprove;
  var completedDate = task.completed_at ? new Date(task.completed_at).toLocaleString() : 'N/A';
  return (
    <div className="flex items-center justify-between p-4 bg-slate-700/30 rounded-lg">
      <div>
        <p className="text-white font-medium">{task.title}</p>
        <p className="text-sm text-gray-400">{task.employee_name} - Completed: {completedDate}</p>
      </div>
      <div className="flex items-center gap-4">
        <span className="text-lg font-bold text-green-400">₹{task.payment_amount}</span>
        <div className="flex gap-2">
          <Button size="sm" onClick={function() { onApprove(task.id, true); }} className="bg-green-600 hover:bg-green-700"><CheckCircle2 className="w-4 h-4 mr-1" />Approve</Button>
          <Button size="sm" variant="outline" onClick={function() { onApprove(task.id, false); }} className="border-red-500/50 text-red-400">Reject</Button>
        </div>
      </div>
    </div>
  );
}

// Weekly Off Tab - Assign weekly off to non-flex employees
function WeeklyOffTab(props) {
  var employees = props.employees;
  var onUpdate = props.onUpdate;
  var [weeklyOffs, setWeeklyOffs] = useState({});
  var [loading, setLoading] = useState(true);
  var [saving, setSaving] = useState({});

  // Filter out flex employees - they don't get weekly off
  var eligibleEmployees = employees.filter(function(e) {
    return e.employee_type !== 'flex_employee';
  });

  useEffect(function() {
    fetchWeeklyOffs();
  }, []);

  function fetchWeeklyOffs() {
    setLoading(true);
    fetch(API_URL + '/api/employee-admin/weekly-offs')
      .then(function(r) { return r.json(); })
      .then(function(data) {
        var offMap = {};
        var employeeList = data.employees || [];
        for (var i = 0; i < employeeList.length; i++) {
          offMap[employeeList[i].id] = employeeList[i].weekly_off_day || 'none';
        }
        setWeeklyOffs(offMap);
        setLoading(false);
      })
      .catch(function() { setLoading(false); });
  }

  function handleSetWeeklyOff(employeeId, day) {
    setSaving(Object.assign({}, saving, { [employeeId]: true }));
    fetch(API_URL + '/api/employee-admin/weekly-off/' + employeeId, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ weekly_off_day: day })
    })
      .then(function(r) { return r.json(); })
      .then(function() {
        toast.success('Weekly off updated');
        setWeeklyOffs(Object.assign({}, weeklyOffs, { [employeeId]: day }));
        setSaving(Object.assign({}, saving, { [employeeId]: false }));
        onUpdate();
      })
      .catch(function() {
        toast.error('Failed to update');
        setSaving(Object.assign({}, saving, { [employeeId]: false }));
      });
  }

  var daysOfWeek = [
    { value: 'none', label: 'No Weekly Off' },
    { value: 'sunday', label: 'Sunday' },
    { value: 'monday', label: 'Monday' },
    { value: 'tuesday', label: 'Tuesday' },
    { value: 'wednesday', label: 'Wednesday' },
    { value: 'thursday', label: 'Thursday' },
    { value: 'friday', label: 'Friday' },
    { value: 'saturday', label: 'Saturday' }
  ];

  var employeeRows = [];
  for (var i = 0; i < eligibleEmployees.length; i++) {
    var emp = eligibleEmployees[i];
    var empId = emp.id;
    var currentDay = weeklyOffs[empId] || 'none';
    var isSaving = saving[empId];
    var typeBadge = emp.employee_type === 'fleet_manager' ? 'bg-blue-500/20 text-blue-300' : 'bg-green-500/20 text-green-300';
    
    var dayOptions = daysOfWeek.map(function(d) {
      return <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>;
    });

    employeeRows.push(
      <div key={emp.id} className="flex items-center justify-between p-4 bg-slate-700/30 rounded-lg" data-testid={'weekly-off-' + emp.id}>
        <div className="flex items-center gap-3">
          <div>
            <p className="text-white font-medium">{emp.name}</p>
            <p className="text-sm text-gray-400">{emp.department || 'N/A'}</p>
          </div>
          <Badge className={typeBadge}>{emp.employee_type.replace(/_/g, ' ')}</Badge>
        </div>
        <div className="flex items-center gap-2">
          <Select 
            value={currentDay} 
            onValueChange={function(empIdCapture) { return function(v) { handleSetWeeklyOff(empIdCapture, v); }; }(empId)}
            disabled={isSaving}
          >
            <SelectTrigger className="bg-slate-700/50 border-white/10 text-white w-40" data-testid={'weekly-off-select-' + emp.id}>
              <SelectValue placeholder="Select day" />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-white/10">
              {dayOptions}
            </SelectContent>
          </Select>
          {isSaving && <div className="w-4 h-4 border-2 border-purple-500/30 border-t-purple-500 rounded-full animate-spin"></div>}
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <Card className="bg-slate-800/50 border-white/10">
        <CardContent className="p-8 flex items-center justify-center">
          <div className="w-8 h-8 border-4 border-purple-500/30 border-t-purple-500 rounded-full animate-spin"></div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-slate-800/50 border-white/10">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2">
          <Calendar className="w-5 h-5 text-red-400" />
          Weekly Off Assignment
        </CardTitle>
        <p className="text-gray-400 text-sm">Assign a weekly off day for each employee. Flex employees are not eligible for weekly off.</p>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {employeeRows}
          {eligibleEmployees.length === 0 && (
            <p className="text-gray-400 text-center py-8">No employees found. Add employees first.</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// Employee Modal
// Employee Modal
function EmployeeModal(props) {
  var isOpen = props.isOpen;
  var onClose = props.onClose;
  var editingEmployee = props.editingEmployee;
  var employeeForm = props.employeeForm;
  var setEmployeeForm = props.setEmployeeForm;
  var onSave = props.onSave;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-slate-800 border-white/10 text-white">
        <DialogHeader><DialogTitle>{editingEmployee ? 'Edit' : 'Add'} Employee</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-sm text-gray-400 mb-1 block">Full Name *</label>
            <Input placeholder="Enter full name" value={employeeForm.name} onChange={function(e) { setEmployeeForm(Object.assign({}, employeeForm, { name: e.target.value })); }} className="bg-slate-700/50 border-white/10 text-white" />
          </div>
          <div>
            <label className="text-sm text-gray-400 mb-1 block">Phone Number * (10 digits)</label>
            <Input placeholder="Enter 10 digit phone" value={employeeForm.phone} onChange={function(e) { setEmployeeForm(Object.assign({}, employeeForm, { phone: e.target.value })); }} className="bg-slate-700/50 border-white/10 text-white" disabled={!!editingEmployee} />
            {!editingEmployee && <p className="text-xs text-gray-500 mt-1">Employee will set password on first login</p>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Department *</label>
              <Select value={employeeForm.department} onValueChange={function(v) { setEmployeeForm(Object.assign({}, employeeForm, { department: v })); }}>
                <SelectTrigger className="bg-slate-700/50 border-white/10 text-white"><SelectValue placeholder="Select department" /></SelectTrigger>
                <SelectContent className="bg-slate-800 border-white/10">
                  <SelectItem value="HR">HR</SelectItem>
                  <SelectItem value="Accounts">Accounts</SelectItem>
                  <SelectItem value="Marketing">Marketing</SelectItem>
                  <SelectItem value="Sales">Sales</SelectItem>
                  <SelectItem value="Operations">Operations</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Position/Type *</label>
              <Select value={employeeForm.employee_type} onValueChange={function(v) { setEmployeeForm(Object.assign({}, employeeForm, { employee_type: v })); }}>
                <SelectTrigger className="bg-slate-700/50 border-white/10 text-white"><SelectValue placeholder="Select position" /></SelectTrigger>
                <SelectContent className="bg-slate-800 border-white/10">
                  <SelectItem value="fleet_manager">Fleet Manager</SelectItem>
                  <SelectItem value="flex_employee">Flex Employee</SelectItem>
                  <SelectItem value="hr">HR</SelectItem>
                  <SelectItem value="digital_marketing">Digital Marketing</SelectItem>
                  <SelectItem value="sales">Sales</SelectItem>
                  <SelectItem value="marketing">Marketing</SelectItem>
                  <SelectItem value="accounts">Accounts</SelectItem>
                  <SelectItem value="operations">Operations</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="text-sm text-gray-400 mb-1 block">Designation (Optional)</label>
            <Input placeholder="e.g., Senior Executive" value={employeeForm.designation} onChange={function(e) { setEmployeeForm(Object.assign({}, employeeForm, { designation: e.target.value })); }} className="bg-slate-700/50 border-white/10 text-white" />
          </div>
        </div>
        <DialogFooter><Button onClick={onSave} className="bg-purple-600 hover:bg-purple-700">Save Employee</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Task Modal with Fleet/Flex/Operational support
function TaskModal(props) {
  var isOpen = props.isOpen;
  var onClose = props.onClose;
  var editingTask = props.editingTask;
  var taskForm = props.taskForm;
  var setTaskForm = props.setTaskForm;
  var employees = props.employees;
  var onSave = props.onSave;

  // Find selected employee to determine task types
  var selectedEmployee = null;
  for (var i = 0; i < employees.length; i++) {
    if (employees[i].id === taskForm.employee_id) {
      selectedEmployee = employees[i];
      break;
    }
  }
  
  var employeeType = selectedEmployee ? selectedEmployee.employee_type : null;
  var isFleetManager = employeeType === 'fleet_manager';
  var isFlexEmployee = employeeType === 'flex_employee';
  var isOperationalEmployee = !isFleetManager && !isFlexEmployee && employeeType;

  var employeeOptions = [];
  for (var j = 0; j < employees.length; j++) {
    var e = employees[j];
    var typeLabel = e.employee_type === 'flex_employee' ? 'Flex' : 
                    e.employee_type === 'fleet_manager' ? 'Fleet' : 
                    e.employee_type.replace('_', ' ');
    employeeOptions.push(<SelectItem key={e.id} value={e.id}>{e.name} ({typeLabel})</SelectItem>);
  }

  // Fleet Manager task types - field work
  var fleetTaskTypes = [
    { value: 'contact_vendor', label: 'Contact Vendor', needsBusiness: true },
    { value: 'visit_restaurants', label: 'Visit Restaurants', needsBusiness: true },
    { value: 'followup_dp_restaurant', label: 'Followup DP/Restaurants', needsBusiness: true },
    { value: 'cover_new_area', label: 'Cover New Area', needsBusiness: false, needsAreaCode: true },
    { value: 'marketing_agency_connect', label: 'Marketing/Advertisement Agency Connect', needsBusiness: true },
    { value: 'distribute_assets', label: 'Distribute Assets to Restaurants/DP', needsBusiness: true }
  ];

  // Flex Employee task types - calling work
  var flexTaskTypes = [
    { value: 'first_call', label: 'First Time Call to Onboard', needsBusiness: true },
    { value: 'follow_up_docs', label: 'Follow Up for Documents', needsBusiness: true },
    { value: 'follow_up_call', label: 'Follow Up Call', needsBusiness: true },
    { value: 'menu_collection', label: 'Menu Collection Call', needsBusiness: true },
    { value: 'onboard', label: 'Direct Onboarding Call', needsBusiness: true },
    { value: 'verification_call', label: 'Verification Call', needsBusiness: true }
  ];

  // Operational Employee task types - office work (NO business details needed for most)
  var operationalTaskTypes = [
    { value: 'onboard_task', label: 'Onboard Task', needsBusiness: false },
    { value: 'payout_task', label: 'Payout Task', needsBusiness: false },
    { value: 'meeting_attend', label: 'Meeting Attend Task', needsBusiness: false },
    { value: 'schedule_meeting', label: 'Schedule Meeting Task', needsBusiness: false },
    { value: 'submit_report', label: 'Submit Report Task', needsBusiness: false },
    { value: 'data_entry', label: 'Data Entry Task', needsBusiness: false },
    { value: 'document_review', label: 'Document Review', needsBusiness: false },
    { value: 'client_follow_up', label: 'Client Follow Up', needsBusiness: true },
    { value: 'vendor_coordination', label: 'Vendor Coordination', needsBusiness: true },
    { value: 'general_task', label: 'General Task', needsBusiness: false }
  ];

  var taskTypes = isFleetManager ? fleetTaskTypes : isFlexEmployee ? flexTaskTypes : operationalTaskTypes;
  var currentTaskType = taskTypes.find(function(t) { return t.value === taskForm.procedure_type; });
  var needsBusinessDetails = currentTaskType ? currentTaskType.needsBusiness : false;
  var needsAreaCode = currentTaskType ? currentTaskType.needsAreaCode : false;

  var taskTypeOptions = taskTypes.map(function(t) {
    return <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>;
  });

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-slate-800 border-white/10 text-white max-w-2xl">
        <DialogHeader>
          <DialogTitle>{editingTask ? 'Edit' : 'Add'} Task</DialogTitle>
          {isFleetManager && <p className="text-sm text-blue-400 mt-1">Fleet Manager - Field Tasks</p>}
          {isFlexEmployee && <p className="text-sm text-amber-400 mt-1">Flex Employee - Calling Tasks</p>}
          {isOperationalEmployee && <p className="text-sm text-green-400 mt-1">{employeeType.replace(/_/g, ' ')} - Operational Tasks</p>}
        </DialogHeader>
        <div className="space-y-4 max-h-96 overflow-y-auto pr-2">
          <div>
            <label className="text-sm text-gray-400 mb-1 block">Assign to Employee *</label>
            <Select value={taskForm.employee_id} onValueChange={function(v) { setTaskForm(Object.assign({}, taskForm, { employee_id: v, procedure_type: '' })); }}>
              <SelectTrigger className="bg-slate-700/50 border-white/10 text-white"><SelectValue placeholder="Select Employee" /></SelectTrigger>
              <SelectContent className="bg-slate-800 border-white/10">{employeeOptions}</SelectContent>
            </Select>
          </div>
          
          {selectedEmployee && (
            <>
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Task Type *</label>
                <Select value={taskForm.procedure_type} onValueChange={function(v) { setTaskForm(Object.assign({}, taskForm, { procedure_type: v })); }}>
                  <SelectTrigger className="bg-slate-700/50 border-white/10 text-white"><SelectValue placeholder="Select task type" /></SelectTrigger>
                  <SelectContent className="bg-slate-800 border-white/10">{taskTypeOptions}</SelectContent>
                </Select>
              </div>

              {needsBusinessDetails && (
                <div className={"p-3 rounded-lg space-y-3 " + (isFleetManager ? "bg-blue-900/30 border border-blue-500/30" : isFlexEmployee ? "bg-amber-900/30 border border-amber-500/30" : "bg-green-900/30 border border-green-500/30")}>
                  <p className={"text-sm font-medium " + (isFleetManager ? "text-blue-400" : isFlexEmployee ? "text-amber-400" : "text-green-400")}>
                    {isFleetManager ? 'Location & Business Details' : isFlexEmployee ? 'Call Details' : 'Contact Details'}
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <Input placeholder="Business/Person Name" value={taskForm.business_name} onChange={function(e) { setTaskForm(Object.assign({}, taskForm, { business_name: e.target.value })); }} className="bg-slate-700/50 border-white/10 text-white" data-testid="task-business-name" />
                    <Select value={taskForm.business_type} onValueChange={function(v) { setTaskForm(Object.assign({}, taskForm, { business_type: v })); }}>
                      <SelectTrigger className="bg-slate-700/50 border-white/10 text-white" data-testid="task-business-type"><SelectValue placeholder="Type" /></SelectTrigger>
                      <SelectContent className="bg-slate-800 border-white/10">
                        <SelectItem value="restaurant">Restaurant</SelectItem>
                        <SelectItem value="delivery_partner">Delivery Partner</SelectItem>
                        {isFleetManager && <SelectItem value="ad_agency">Ad Agency</SelectItem>}
                        {isOperationalEmployee && <SelectItem value="vendor">Vendor</SelectItem>}
                        {isOperationalEmployee && <SelectItem value="client">Client</SelectItem>}
                      </SelectContent>
                    </Select>
                  </div>
                  <Input placeholder="Contact Number" value={taskForm.contact_number} onChange={function(e) { setTaskForm(Object.assign({}, taskForm, { contact_number: e.target.value })); }} className="bg-slate-700/50 border-white/10 text-white" data-testid="task-contact-number" />
                  <Input placeholder="Address / Location" value={taskForm.business_location} onChange={function(e) { setTaskForm(Object.assign({}, taskForm, { business_location: e.target.value })); }} className="bg-slate-700/50 border-white/10 text-white" data-testid="task-business-location" />
                </div>
              )}

              {needsAreaCode && (
                <div className="p-3 rounded-lg space-y-3 bg-blue-900/30 border border-blue-500/30">
                  <p className="text-sm font-medium text-blue-400">Area Details</p>
                  <Textarea placeholder="Enter area with code (e.g., Area-123 Sector 5, Downtown)" value={taskForm.business_location} onChange={function(e) { setTaskForm(Object.assign({}, taskForm, { business_location: e.target.value })); }} className="bg-slate-700/50 border-white/10 text-white" rows={2} data-testid="task-area-details" />
                </div>
              )}

              <Input placeholder="Task Title *" value={taskForm.title} onChange={function(e) { setTaskForm(Object.assign({}, taskForm, { title: e.target.value })); }} className="bg-slate-700/50 border-white/10 text-white" />
              <Textarea placeholder="Instructions / Notes" value={taskForm.description} onChange={function(e) { setTaskForm(Object.assign({}, taskForm, { description: e.target.value })); }} className="bg-slate-700/50 border-white/10 text-white" rows={2} />
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">{isFlexEmployee ? 'Priority (Potential Earning)' : 'Priority'}</label>
                  <Select value={taskForm.priority} onValueChange={function(v) { setTaskForm(Object.assign({}, taskForm, { priority: v })); }}>
                    <SelectTrigger className="bg-slate-700/50 border-white/10 text-white"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-slate-800 border-white/10">
                      <SelectItem value="low">{isFlexEmployee ? 'Low - ₹50' : 'Low'}</SelectItem>
                      <SelectItem value="medium">{isFlexEmployee ? 'Medium - ₹60' : 'Medium'}</SelectItem>
                      <SelectItem value="high">{isFlexEmployee ? 'High - ₹70' : 'High'}</SelectItem>
                      <SelectItem value="urgent">{isFlexEmployee ? 'Urgent - ₹80' : 'Urgent'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Due Date</label>
                  <Input type="date" value={taskForm.due_date} onChange={function(e) { setTaskForm(Object.assign({}, taskForm, { due_date: e.target.value })); }} className="bg-slate-700/50 border-white/10 text-white" />
                </div>
              </div>

              {/* File Attachment */}
              <div className="p-3 rounded-lg bg-slate-700/30 border border-white/10">
                <label className="text-sm text-gray-400 mb-2 block flex items-center gap-2">
                  <Paperclip className="w-4 h-4" />
                  Attach File (Optional)
                </label>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.xlsx,.xls,.csv,.doc,.docx"
                  onChange={function(e) { setTaskForm(Object.assign({}, taskForm, { attachment: e.target.files[0] })); }}
                  className="w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-purple-600 file:text-white file:cursor-pointer hover:file:bg-purple-700"
                  data-testid="task-attachment-input"
                />
                {taskForm.attachment && (
                  <p className="text-xs text-green-400 mt-2">Selected: {taskForm.attachment.name}</p>
                )}
                <p className="text-xs text-gray-500 mt-1">PDF, Images, Excel, Word (max 10MB)</p>
              </div>
            </>
          )}
          
          {!selectedEmployee && (
            <div className="p-8 text-center text-gray-400">
              <p>Select an employee to see task options</p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button onClick={onSave} disabled={!selectedEmployee} className="bg-purple-600 hover:bg-purple-700">Save Task</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Slot Configuration Modal
function SlotModal(props) {
  var isOpen = props.isOpen;
  var onClose = props.onClose;
  var slotDate = props.slotDate;
  var setSlotDate = props.setSlotDate;
  var predefinedSlots = props.predefinedSlots;
  var slotSeats = props.slotSeats;
  var setSlotSeats = props.setSlotSeats;
  var onCreate = props.onCreate;

  var slotItems = [];
  for (var i = 0; i < predefinedSlots.length; i++) {
    var slot = predefinedSlots[i];
    var idx = i;
    slotItems.push(
      <div key={idx} className="p-3 bg-slate-700/30 rounded-lg">
        <p className="text-white text-sm font-medium mb-2">{slot.label}</p>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">Seats:</span>
          <Input 
            type="number" 
            min="0" 
            max="50" 
            value={slotSeats[idx] || 0} 
            onChange={(function(index) { return function(e) { var newSeats = Object.assign({}, slotSeats); newSeats[index] = parseInt(e.target.value) || 0; setSlotSeats(newSeats); }; })(idx)}
            className="bg-slate-600/50 border-white/10 text-white w-20 h-8 text-sm" 
          />
        </div>
      </div>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-slate-800 border-white/10 text-white max-w-2xl">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Clock className="w-5 h-5 text-amber-400" />Configure Daily Slots</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-sm text-gray-400 mb-1 block">Select Date</label>
            <Input type="date" value={slotDate} onChange={function(e) { setSlotDate(e.target.value); }} className="bg-slate-700/50 border-white/10 text-white" />
          </div>
          <p className="text-sm text-gray-400">Set seat limits for each time slot. Leave at 0 to disable a slot.</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-h-80 overflow-y-auto">
            {slotItems}
          </div>
        </div>
        <DialogFooter><Button onClick={onCreate} className="bg-amber-600 hover:bg-amber-700">Create Slots</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Report Modal
function ReportModal(props) {
  var isOpen = props.isOpen;
  var onClose = props.onClose;
  var reportDates = props.reportDates;
  var setReportDates = props.setReportDates;
  var reportData = props.reportData;
  var onGenerate = props.onGenerate;
  var onDownload = props.onDownload;

  var recordItems = [];
  if (reportData && reportData.records) {
    var recordsToShow = reportData.records.slice(0, 15);
    for (var i = 0; i < recordsToShow.length; i++) {
      var r = recordsToShow[i];
      var hoursDisplay = r.hours_worked ? r.hours_worked.toFixed(2) + 'h' : '-';
      recordItems.push(
        <div key={i} className="flex justify-between p-2 bg-slate-700/30 rounded text-sm">
          <span className="text-white">{r.date} - {r.employee_name}</span>
          <span className="text-gray-400">{hoursDisplay}</span>
        </div>
      );
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-slate-800 border-white/10 text-white max-w-2xl">
        <DialogHeader><DialogTitle>Attendance Report</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="flex gap-4">
            <Input type="date" value={reportDates.start} onChange={function(e) { setReportDates(Object.assign({}, reportDates, { start: e.target.value })); }} className="bg-slate-700/50 border-white/10 text-white" />
            <Input type="date" value={reportDates.end} onChange={function(e) { setReportDates(Object.assign({}, reportDates, { end: e.target.value })); }} className="bg-slate-700/50 border-white/10 text-white" />
            <Button onClick={onGenerate} className="bg-purple-600">Generate</Button>
            <Button onClick={onDownload} variant="outline" className="border-white/20 text-white"><Download className="w-4 h-4" /></Button>
          </div>
          {reportData && (
            <div>
              <div className="grid grid-cols-4 gap-4 mb-4">
                <div className="p-3 bg-slate-700/30 rounded text-center"><p className="text-xl font-bold text-white">{reportData.summary.total_records}</p><p className="text-xs text-gray-400">Records</p></div>
                <div className="p-3 bg-slate-700/30 rounded text-center"><p className="text-xl font-bold text-white">{reportData.summary.total_hours.toFixed(1)}h</p><p className="text-xs text-gray-400">Hours</p></div>
                <div className="p-3 bg-slate-700/30 rounded text-center"><p className="text-xl font-bold text-white">{reportData.summary.completed_shifts}</p><p className="text-xs text-gray-400">Completed</p></div>
                <div className="p-3 bg-slate-700/30 rounded text-center"><p className="text-xl font-bold text-white">{reportData.summary.average_hours_per_shift.toFixed(1)}h</p><p className="text-xs text-gray-400">Avg/Shift</p></div>
              </div>
              <div className="max-h-48 overflow-y-auto space-y-2">
                {recordItems}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
